import { useRef, useState } from "react";
import { Upload } from "lucide-react";

/**
 * BrowseBar — reusable file drop-zone shared by every upload spot in Le Desk.
 *
 * Spec rev153 (2026-02-07) — style unifié bleu :
 *   • Fond bleu foncé #0F1E2E, bord pointillé bleu #0078D4 (1.5px).
 *   • Icône + texte bleu clair #5B9EE8.
 *   • Icône UPLOAD au-dessus du texte "Glisser-déposer ou cliquer pour
 *     charger" — layout vertical (~92px de haut).
 *   • État "drag-over" → fond plus dense (#132A44) + halo bleu.
 *
 * • Whole zone is clickable (cursor: pointer) — no separate button.
 * • Native drag & drop.
 * • Emits every File selected/dropped via `onFiles(files: File[])`.
 *
 * Spec 2026-02-17 bug-fix : the hidden `<input type="file">` used to sit
 * under `className="hidden"` (`display: none`). On some Chrome/Windows
 * combinations the file picker failed to open after a drag-hover on the
 * zone, because the input was fully out of the render tree. It's now kept
 * in the DOM but visually hidden (opacity 0 + zero size + off-screen) so
 * `input.click()` reliably opens the OS file dialog. Also stops
 * propagation on the drop container's click so nested consumers (Dialog
 * overlays, modal wrappers) never re-trigger the picker twice.
 */
export default function BrowseBar({
  accept = "*/*",
  multiple = true,
  onFiles,
  testIdPrefix = "browsebar",
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length) onFiles?.(list);
  };

  const openPicker = (e) => {
    // Guard against double-fire from a nested overlay's click bubbling.
    e?.stopPropagation?.();
    // Some browsers require the click to happen inside the same tick as
    // the user gesture; the ref call is synchronous so this is fine.
    inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker(e);
        }
      }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      className="cursor-pointer select-none transition-all relative w-full"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        minHeight: 92,
        padding: "16px 12px",
        borderRadius: 10,
        border: `1.5px dashed #0078D4`,
        background: dragOver ? "#132A44" : "#0F1E2E",
        boxShadow: dragOver ? "0 0 0 3px rgba(0,120,212,0.20)" : "none",
      }}
      data-testid={testIdPrefix}
    >
      <Upload size={22} style={{ color: "#5B9EE8", flexShrink: 0 }} strokeWidth={2} />
      <div className="text-[12.5px] leading-tight text-center">
        <span style={{ color: "#5B9EE8", fontWeight: 600 }}>
          Glisser-déposer ou cliquer pour charger
        </span>
        {multiple && (
          <span style={{ color: "rgba(91,158,232,0.60)", fontWeight: 400 }}>
            {" — plusieurs fichiers acceptés"}
          </span>
        )}
      </div>
      {/* Kept in the DOM (not display:none) so input.click() reliably
          opens the OS file dialog on all Chrome / Edge / Safari builds.
          Positioned absolutely so it doesn't affect layout. */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          handleFiles(e.target.files);
          // Reset so the same file can be picked again after removal.
          e.target.value = "";
        }}
        style={{
          position: "absolute",
          left: 0, top: 0,
          width: 1, height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
        tabIndex={-1}
        aria-hidden="true"
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
}
